/**
 * appearance.ts
 * The field app is light by default: high contrast for the plateau at noon,
 * whatever the phone's system theme says (operator-visual-language). Two
 * per-device choices live in the profile sheet on Hoy (ProfileSheet.vue):
 *   - Noche: the dark theme, for the night shift. Toggles the `.dark` class
 *     that @suite/ui's class-based dark: utilities read (style.css).
 *   - Modo campo: bigger type and touch targets for gloves. Toggles the
 *     `.field` class, which raises the root font size so every rem-based
 *     size grows with it.
 * Stored in localStorage as a convenience only; when storage is blocked the
 * choice still applies for the current visit.
 */
import { ref } from 'vue';

const NIGHT_KEY = 'rigx.appearance.night';
const FIELD_KEY = 'rigx.appearance.field';

export const nightMode = ref(false);
export const fieldMode = ref(false);

function read(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function write(key: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    // Storage blocked (private mode): the choice lasts this visit only.
  }
}

function sync(): void {
  const root = document.documentElement;
  root.classList.toggle('dark', nightMode.value);
  root.classList.toggle('field', fieldMode.value);
}

export function applyAppearance(): void {
  nightMode.value = read(NIGHT_KEY);
  fieldMode.value = read(FIELD_KEY);
  sync();
}

export function setNightMode(on: boolean): void {
  nightMode.value = on;
  write(NIGHT_KEY, on);
  sync();
}

export function setFieldMode(on: boolean): void {
  fieldMode.value = on;
  write(FIELD_KEY, on);
  sync();
}
